/* eslint-disable no-duplicate-case */
import React, { useEffect } from "react";
import { Switch, Redirect, useLocation } from "react-router-dom";
import Enrollment from "Pages/Enrollment/Enrollment";
import EnrollmentFromServer from "Pages/Enrollment/EnrollmentFromServer";
import OnlineEnrollment from "Pages/Enrollment/OnlineEnrollment";
import Dashboard from "Pages/Dashboard/Dashboard";
import Response from "Pages/Response/Response";
import FundsPage from "Pages/FundsPage/FundsPage";
import MyAccounts from "Pages/MyAccounts/MyAccounts";
import ManageLimit from "Pages/ManageLimit/ManageLimit";
import Loading from "Components/Loading";
import ValidUserRoute from "Utils/Routes/ValidUserRoute";
import NotEnrolledUserRoute from "Utils/Routes/NotEnrolledUserRoute";
import TransactionRoute from "Utils/Routes/TransactionRoute";
import LimitEnabledRoute from "Utils/Routes/LimitEnabledRoute";
import Enrolled from "Utils/Routes/Enrolled";
import useSessionIdWatcher from "Utils/Hooks/useSessionIdWatcher";
import AddAccount from "./MyAccounts/AddAccount";
import DeleteAccount from "./MyAccounts/DeleteAccount";
import { VipAccountProvider } from "../context/VipAccountContext";
import "./App.scss";
import "./Theme.scss";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useView, { VIEW } from "Utils/Hooks/useView";
import useTransactionAmount from "Utils/Hooks/useTransactionAmount";
import useFundType from "Utils/Hooks/useFundType";
import useRedirectUrl from "Utils/Hooks/useRedirectUrl";
import useCancelRedirect from "Utils/Hooks/useCancelRedirect";
import { APP_ROOT } from "Utils/EnvVars";
import { useFetch } from "Utils/Hooks/useFetch";
import {
  IPADDRESSURL,
  PRODUCTTYPES,
  FUND_SOURCE_TYPE,
  Routes,
} from "Utils/Constants";
import useNative from "Utils/Hooks/useNative";
import useOAuthStateId from "Utils/Hooks/useOAuthStateId";
import useAppKey from "Utils/Hooks/useAppKey";
import { faro, FaroRoute } from "@grafana/faro-react";
import Header from "Components/Header/Header";
import { Box } from "@mui/material";
import Footer from "Components/Footer/Footer";
import TermsAndConditions from "Components/TermsAndConditions";
import { useLastEnrollmentStep } from "context/LastEnrollmentStepProvider";
import AddAccountSuccess from "Components/AddAccountSuccess";

function App() {
  const isInIframe = window.self !== window.top;
  const mode = useMode(); // Cache mode selection
  const view = useView(); // Cache view selection
  useFundType(); // Cache fund type
  useFetch(IPADDRESSURL);
  useNative();
  useRedirectUrl();
  useCancelRedirect();
  useOAuthStateId();
  useAppKey();
  const watcher = useSessionIdWatcher();
  const session = watcher.session;
  const sessionError = watcher.error;
  const sessionId = watcher.sessionId;
  const logSession = watcher.logSession;
  const isCashierPage = view === VIEW.cashier;
  const isOAuthRedirect = window.location.href.includes("oauth_state_id=");
  const { LastEnrollmentStep } = useLastEnrollmentStep();

  useTransactionAmount(session?.transactionAmount); // Cache transaction amount

  useEffect(() => {
    const tabCloseEvent = () => {
      const isRedirectOrCancel = window.sessionStorage.getItem(
        "IsRedirectOrCancel",
      )
        ? window.sessionStorage.getItem("IsRedirectOrCancel")
        : false;
      const isPlaidLaunch = window.sessionStorage.getItem("plaidlaunch")
        ? window.sessionStorage.getItem("plaidlaunch")
        : false;
      faro.api.pushEvent("User exited flow", { method: "Tab Close Event" });
      if (!isRedirectOrCancel && !isPlaidLaunch) {
        const body = new Blob(
          [
            JSON.stringify({
              transactionStage:
                mode === MODE.withdraw
                  ? "Withdrawal"
                  : mode === MODE.manageBanks
                    ? "AddFundingSource"
                    : "Deposit",
              sessionId: sessionId ?? sessionId,
            }),
          ],
          { type: "application/json" },
        );
        navigator.sendBeacon(APP_ROOT + "/api/usertracking/cancelflow", body);
      }
    };

    window.addEventListener("beforeunload", (event) => {
      if (event.persisted === false) {
        tabCloseEvent();
      }
    });

    window.addEventListener("pagehide", (event) => {
      if (event.persisted === false) {
        tabCloseEvent();
      }
    });

    return () => {
      window.removeEventListener("beforeunload", tabCloseEvent);
      window.removeEventListener("pagehide", tabCloseEvent);
    };
  }, []);

  useEffect(() => {
    const updateSession = () => {
      const userSession = {
        ...logSession,
        attributes: {
          ...logSession.attributes,
          mode: isCashierPage ? "Cashier Mode" : "Patron Mode",
        },
      };
      faro.api.setSession(userSession);
    };

    if (logSession !== null && sessionId) {
      updateSession();
    }
  }, [isCashierPage, logSession, sessionId]);

  const location = useLocation();

  if (watcher.isLoading) return <Loading />;
  const isVipEnrolled = session?.isVipEnrolled;
  const withdrawFlow = mode === MODE.withdraw;
  const depositFlow = mode === MODE.deposit;
  const manageFundingSources = mode === MODE.manageBanks;
  const expiredMessage = session?.operatorName
    ? `This session has expired. To make a new ${withdrawFlow ? "withdrawal" : "deposit"}, please go back to ${session.operatorName}.`
    : "Your session has expired or could not be found. Please close this window.";
  const sessionInvalid =
    sessionError ||
    !session ||
    session?.isExpired ||
    (session?.expires && Date.parse(session.expires) <= Date.now());
  const isError =
    session && session.errorModel && session.errorModel.patronErrorMessage;
  const errorMessage =
    session && session.errorModel && session.errorModel.patronErrorMessage
      ? session.errorModel.patronErrorMessage
      : "";
  const productType = session?.productType;
  document.title =
    session?.patronProductType === PRODUCTTYPES.PREFERRED ? "VIPP" : "VIPO";
  const cashierDepositUrl = "/cashierDeposit";
  const cashierWithdrawUrl = "/cashierWithdraw";
  const appStyle = isCashierPage ? "cashierPageApp" : "app";
  const isCashierRedirect =
    !isOAuthRedirect &&
    session?.uiStartingAction === FUND_SOURCE_TYPE.PLAID &&
    (session?.uiStartingPath?.includes(Routes.ADD_ACCOUNT) ||
      session?.uiStartingPath?.includes(Routes.MY_ACCOUNTS));
  const isOperatorPreferred = session?.operatorProducts.includes(
    PRODUCTTYPES.PREFERRED,
  );
  const isOperatorOnline = session?.operatorProducts.includes(
    PRODUCTTYPES.ONLINE,
  );
  const isOperatorBoth = isOperatorOnline && isOperatorPreferred;
  const isVerifyDevice =
    session?.threatMetrixEnabled &&
    session?.toggles.threatMetrixCallOnThreshold;

  const fundsPages = [
    "/addFunds",
    "/withdrawFunds",
    "/cashierDeposit",
    "/cashierWithdraw",
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      <VipAccountProvider>
        {!isInIframe && <Header isEnrollment={!session?.isVipEnrolled} />}
        <Box
          className={appStyle}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100vw",
          }}
        >
          <Box
            sx={{
              // width and padding to offset iframe scrollbar
              width: isCashierPage ? "95%" : "325px",
              paddingRight: isCashierPage ? "15px" : "0px",
            }}
          >
            {/* <FaroRouter basename={APP_ROOT.replace(/\/$/, "")}> */}
            <Switch>
              {/* Index router */}
              <FaroRoute
                exact
                path="/"
                render={() => {
                  if (isError) {
                    return <Redirect to="/error" />;
                  } else if (manageFundingSources && isVipEnrolled) {
                    return <Redirect to="/myAccounts" />;
                  } else if (isVipEnrolled && isVerifyDevice) {
                    return <Redirect to="/deviceverification" />;
                  } else if (isOAuthRedirect && sessionInvalid) {
                    return <Redirect to="/goback" />;
                  } else if (sessionInvalid) {
                    return <Redirect to="/expired" />;
                  } else if (
                    !isVipEnrolled &&
                    (productType === PRODUCTTYPES.ONLINE ||
                      session?.toggles.productRoutingEnabled) &&
                    !isCashierRedirect &&
                    (isOperatorBoth || isOperatorOnline)
                  ) {
                    return <Redirect to="/onlineEnroll" />;
                  } else if (!isVipEnrolled && !isCashierRedirect) {
                    return <Redirect to="/enroll" />;
                  } else if (isCashierRedirect) {
                    return <Redirect to={session.uiStartingPath} />;
                  } else if (isOAuthRedirect) {
                    return <Redirect to="/addAccount" />;
                  } else if (isVipEnrolled && withdrawFlow) {
                    if (isCashierPage) {
                      return <Redirect to={cashierWithdrawUrl} />;
                    } else {
                      return <Redirect to="/withdrawFunds" />;
                    }
                  } else if (isVipEnrolled && depositFlow) {
                    if (isCashierPage) {
                      return <Redirect to={cashierDepositUrl} />;
                    } else {
                      return <Redirect to="/addFunds" />;
                    }
                  } else {
                    return <Redirect to="/invalid" />;
                  }
                }}
              />
              {/* Main application */}
              <NotEnrolledUserRoute
                path="/enroll"
                component={EnrollmentFromServer}
                appProps={{ plaidRedirect: isOAuthRedirect }}
              />
              <NotEnrolledUserRoute path="/retry" component={Enrollment} />
              <NotEnrolledUserRoute
                path="/onlineEnroll"
                component={OnlineEnrollment}
                appProps={{ plaidRedirect: isOAuthRedirect }}
              />
              <Enrolled path="/dashboard" component={Dashboard} />
              <TransactionRoute path="/addFunds" component={FundsPage} />
              <TransactionRoute
                path="/withdrawFunds"
                component={FundsPage}
                appProps={{ withdraw: true }}
              />
              <TransactionRoute path="/cashierDeposit" component={FundsPage} />
              <TransactionRoute
                path="/cashierWithdraw"
                component={FundsPage}
                appProps={{ withdraw: true }}
              />
              <ValidUserRoute path="/myAccounts" component={MyAccounts} />
              <ValidUserRoute
                path="/addAccount"
                component={AddAccount}
                appProps={{
                  plaidRedirect: isOAuthRedirect,
                  isCashierRedirect: isCashierRedirect,
                }}
              />
              <ValidUserRoute
                path="/addAccountSuccess"
                component={AddAccountSuccess}
                appProps={{ bankManagementMode: true }}
              />
              <ValidUserRoute path="/deleteAccount" component={DeleteAccount} />
              <LimitEnabledRoute path="/manageLimit" component={ManageLimit} />
              <FaroRoute exact path="/expired">
                <Response
                  isError={true}
                  onClickClose={true}
                  title="Session Expired"
                  message={expiredMessage}
                  customButtons={[
                    {
                      label: session?.operatorName
                        ? `Continue To ${session.operatorName}`
                        : "Close",
                      returnToOperator: true,
                      returnUrl: session?.returnUrl,
                      transactionId: session?.transactionId,
                      transactionType:
                        mode === MODE.manageBanks
                          ? "bankManagement"
                          : withdrawFlow
                            ? "withdraw"
                            : "deposit",
                    },
                  ]}
                />
              </FaroRoute>
              <FaroRoute exact path="/deviceverification">
                <Response
                  title="Device Verification"
                  customButtons={[
                    {
                      returnToOperator: true,
                      returnUrl: session?.returnUrl,
                      transactionId: session?.transactionId,
                      transactionType:
                        mode === MODE.manageBanks
                          ? "bankManagement"
                          : withdrawFlow
                            ? "withdraw"
                            : "deposit",
                    },
                  ]}
                />
              </FaroRoute>
              <FaroRoute exact path="/deviceverificationerror">
                <Response
                  showButton={false}
                  showHeader={false}
                  title="Device Verification Error"
                  messageTop="We are unable to verify your device with the phone number on file."
                  messageBottom="Please contact our Customer Service Team at 1-800-500-1973."
                />
              </FaroRoute>
              <FaroRoute exact path="/goback">
                <Response
                  isBack={true}
                  title="Plaid Linked"
                  message="Press the Back button to complete bank setup."
                />
              </FaroRoute>
              <FaroRoute exact path="/submit">
                <Response isError={false} />
              </FaroRoute>
              <FaroRoute exact path="/success">
                <Response
                  isError={false}
                  title="Success"
                  message="You've successfully completed this action."
                />
              </FaroRoute>
              <FaroRoute exact path="/error">
                <Response
                  isError={true}
                  onClickClose={true}
                  message={errorMessage}
                />
              </FaroRoute>
              <FaroRoute exact path="/zerobalance">
                <Response onClickClose={true} title="Insufficient Funds" />
              </FaroRoute>
              <FaroRoute exact path="/invalid">
                <Response
                  onClickClose={true}
                  title="Invalid Entry"
                  showHeader={false}
                  message={
                    session?.operatorName
                      ? `Please access VIP Preferred from ${session.operatorName}.`
                      : "Please close this window."
                  }
                  customButtons={[
                    {
                      label: session?.operatorName
                        ? `Continue To ${session.operatorName}`
                        : "Close",
                      returnToOperator: true,
                      returnUrl: session?.returnUrl,
                      transactionId: session?.transactionId,
                      transactionType:
                        mode === MODE.manageBanks
                          ? "bankManagement"
                          : withdrawFlow
                            ? "withdraw"
                            : "deposit",
                    },
                  ]}
                />
              </FaroRoute>
              <FaroRoute exact path="/logout">
                <Response
                  isError={false}
                  title="Logged Out!"
                  loggedOut={true}
                  onClickClose={true}
                  showHeader={false}
                  message="You've been successfully logged out."
                  customButtons={[
                    {
                      label: session?.operatorName
                        ? `Continue To ${session.operatorName}`
                        : "Close",
                      returnToOperator: true,
                      returnUrl: session?.returnUrl,
                      transactionId: session?.transactionId,
                      transactionType:
                        mode === MODE.manageBanks
                          ? "bankManagement"
                          : withdrawFlow
                            ? "withdraw"
                            : "deposit",
                    },
                  ]}
                />
              </FaroRoute>
              <FaroRoute exact path="/inprogress">
                <Response
                  isError={false}
                  showHeader={false}
                  message="Mitek Verification is in progress"
                ></Response>
              </FaroRoute>
              <FaroRoute render={() => <Redirect to="/dashboard" />} />
            </Switch>
          </Box>
        </Box>
        {(location.pathname === "/myAccounts" ||
          location.pathname === "/addAccount" ||
          location.pathname === "/manageLimit" ||
          location.pathname === "/dashboard" ||
          (fundsPages.includes(location.pathname) && !isCashierPage)) && (
          <Footer isCashier={isCashierPage} />
        )}
        {(location.pathname === "/cashierWithdraw" ||
          location.pathname === "/withdrawFunds") && (
          <TermsAndConditions
            buttonText={"withdraw"}
            isCashier={isCashierPage}
          />
        )}
        {(location.pathname === "/addFunds" ||
          location.pathname === "/cashierDeposit") && (
          <TermsAndConditions
            buttonText={"deposit"}
            isCashier={isCashierPage}
          />
        )}
        {(location.pathname === "/enroll" ||
          location.pathname === "/onlineEnroll") &&
          LastEnrollmentStep && (
            <TermsAndConditions isCashier={isCashierPage} buttonText="submit" />
          )}
      </VipAccountProvider>
    </Box>
  );
}

export default App;
